import React, { useRef } from "react";
import { Locate, Home, MapPin, Loader2 } from "lucide-react";
import { Autocomplete } from "@react-google-maps/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

function Choice({ icon, title, active, disabled, onClick, testid, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`text-left rounded-md border min-h-[44px] p-3 transition ${
        active ? "bg-amber-50 border-amber-300 ring-2 ring-amber-200" : "bg-white border-slate-200 hover:bg-slate-50"
      } ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
      data-testid={testid}
    >
      <div className="text-sm font-medium text-slate-900 flex items-center gap-2">
        {icon} {title}
      </div>
      {children}
    </button>
  );
}

function GeoStatus({ geoState, geoCoords, geoError }) {
  if (geoState === "requesting") {
    return (
      <span className="text-slate-500 inline-flex items-center gap-1">
        <Loader2 className="h-3 w-3 animate-spin" /> Requesting browser permission…
      </span>
    );
  }
  if (geoState === "granted" && geoCoords) {
    return <span className="text-emerald-700">Got it: {geoCoords.lat.toFixed(5)}, {geoCoords.lng.toFixed(5)}</span>;
  }
  if (geoState === "denied") return <span className="text-red-600">{geoError || "Permission denied"}</span>;
  if (geoState === "unsupported") return <span className="text-red-600">Browser doesn't support geolocation</span>;
  return <span className="text-slate-500">Click to allow GPS</span>;
}

function CustomAddressInput({ opt, gmapsLoaded, testid }) {
  const acRef = useRef(null);
  const onKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      opt.commitCustomAddress(e.currentTarget.value);
    }
  };
  const input = (
    <Input
      value={opt.customAddress}
      onChange={(e) => opt.setCustomAddress(e.target.value)}
      onKeyDown={onKeyDown}
      placeholder={gmapsLoaded ? "Start typing an address…" : "e.g. 123 Main St, Saskatoon, SK"}
      className="mt-2"
      onClick={(e) => e.stopPropagation()}
      data-testid={testid}
    />
  );
  if (!gmapsLoaded) return input;
  return (
    <Autocomplete
      onLoad={(ac) => {
        acRef.current = ac;
        try {
          ac.setBounds(new window.google.maps.LatLngBounds({ lat: 52.05, lng: -106.85 }, { lat: 52.25, lng: -106.5 }));
        } catch (_) {}
      }}
      onPlaceChanged={() => {
        const place = acRef.current && acRef.current.getPlace();
        if (!place || !place.formatted_address) return;
        const loc = place.geometry && place.geometry.location;
        const coords = loc ? { lat: loc.lat(), lng: loc.lng() } : null;
        opt.commitCustomAddress(place.formatted_address, coords);
      }}
      options={{ componentRestrictions: { country: "ca" }, fields: ["formatted_address", "geometry"] }}
    >
      {input}
    </Autocomplete>
  );
}

export default function StartPointPicker({ opt, homeAddress, testIdPrefix: p }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-wider text-slate-500">Start point</Label>
      <div className="flex flex-col gap-2 mt-2">
        <Choice
          icon={<Locate className="h-4 w-4" />}
          title="Use my current location (GPS)"
          active={opt.startMode === "geo"}
          onClick={() => opt.setStartMode("geo")}
          testid={`${p}-start-geo`}
        >
          <div className="text-xs mt-1">
            {opt.startMode === "geo" ? (
              <GeoStatus geoState={opt.geoState} geoCoords={opt.geoCoords} geoError={opt.geoError} />
            ) : (
              <span className="text-slate-500">Best for use-on-the-go. The browser will ask for permission.</span>
            )}
          </div>
        </Choice>
        <Choice
          icon={<Home className="h-4 w-4" />}
          title="Start from my home/office"
          active={opt.startMode === "home"}
          disabled={!homeAddress}
          onClick={() => opt.setStartMode("home")}
          testid={`${p}-start-home`}
        >
          <div className="text-xs text-slate-500 mt-0.5">
            {homeAddress ? homeAddress : <span className="italic">Set this in Settings → Profile to enable.</span>}
          </div>
        </Choice>
        <Choice
          icon={<MapPin className="h-4 w-4" />}
          title="One-off custom address"
          active={opt.startMode === "custom"}
          onClick={() => opt.setStartMode("custom")}
          testid={`${p}-start-custom`}
        >
          {opt.startMode === "custom" ? (
            <>
              <CustomAddressInput opt={opt} gmapsLoaded={opt.gmapsLoaded} testid={`${p}-custom-address-input`} />
              <div className="text-[11px] text-slate-500 mt-1">
                Pick a suggestion or press Enter to set the start point.
              </div>
            </>
          ) : (
            <div className="text-xs text-slate-500 mt-0.5">Override the start point for this trip only.</div>
          )}
        </Choice>
      </div>
    </div>
  );
}
